// Phase 1 of installing Blake's demo (recon/15-installation-plan.md; Phase 0
// spike commit 3a6e5112 answered the CSS-leak falsifier; recon
// 16-adversarial-review-phase1.md found the fail-open/silent-failure defects
// this version fixes). This is the REAL mount: rendered UNCONDITIONALLY,
// once, at the app's root (see App.tsx), outside the router's per-route
// Switch — never mounted/unmounted by navigation. Only its CSS visibility
// toggles with the current route.
//
// WHY UNCONDITIONAL MOUNT + VISIBILITY TOGGLE (not mount-on-enter /
// unmount-on-leave, which is what the spike did): Blake's data.js/app.js are
// classic scripts with top-level `const` declarations. Injecting their
// <script> tags a SECOND time re-declares those consts and throws
// (`Identifier 'X' has already been declared` — recon spike, burn #8). The
// only way to guarantee "never inject twice" is to never let a re-entry to
// this route trigger the injection path again — so the container (and the
// #app node app.js's own render() writes into) is never destroyed, and the
// one-time boot is guarded by a module-level flag, not by mount/unmount
// lifecycle. Leaving and returning to /web only flips `display`.
//
// WHY THE HEAVY LOAD IS DEFERRED, NOT EAGER AT COLD APP START: this component
// itself mounts at true app boot (so the DOM node app.js needs already
// exists), but injecting ~500KB of JS/CSS and firing a listings fetch for
// every visitor to the site who never opens /web would be wasteful. "Load
// once at app boot, not per route" is read here as "load once, ever, for
// this tab — on first arrival, not on every visit" — the fix for the spike's
// actual bug (repeat visits re-injecting) without eagerly paying the cost for
// people who never see this surface.
import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/context/AuthContext";
import { resolveFlags, ANONYMOUS_ROLLOUT_ID } from "@/hooks/use-feature-flags";
import {
  installFloorListings,
  resetAuthPrefill,
  bridgeScriptSource,
  appFixtureStripSource,
  installRealDiscoverSearch,
  installLiveListingComments,
  installPriceCheckRun,
  installWizardPriceCheck,
  installWallet,
  installDeals,
  installDealThreadLoader,
  installWants,
  installAccount,
  installNotifications,
  signOutAndReload,
} from "./floorTransport";
import { installWantsWrite } from "./wantsWrite";
import { installLinkFixups } from "./linkFixups";
import { installAvatarFallback } from "./avatarFallback";
import { installPromo } from "./promoInstall";
import { installPromoOpenGate } from "./promoOpenGate";
import { installDealActions } from "./dealActions";
import { installInPerson } from "./inPerson";
import { installDealCounter } from "./dealCounter";
import { installMakeOffer } from "./makeOffer";
import { installDisputeFile } from "./disputeFile";
import { installReceiptView } from "./receiptView";
import { installShipDropOff } from "./shipDropOff";
import { installDealMessaging } from "./dealMessaging";
import { installDealThreadPoll } from "./dealThreadPoll";
import { installDealsReadState } from "./dealsReadState";
import { installNotificationsRead } from "./notificationsRead";
import { installScopeRescue } from "./scopeRescue";
import { installAuthSkipReload } from "./authSkipReload";
import { installExchangePrefill } from "./exchangePrefill";
import { installExchangeSend } from "./exchangeSend";
import { installWriteGuard } from "./writeGuard";
import { installCheckoutCtaGuard } from "./checkoutCtaGuard";
import { installCheckoutContext, installCheckoutRouteEntry } from "./checkoutContext";
import { installAccountActions } from "./accountActions";
import { installWizardPublish } from "./wizardPublish";
import { installWizardScan } from "./wizardScan";
import { installCommentPost } from "./commentPost";
import { installListingVotes } from "./listingVotes";
import { installStripeConnectLaunch } from "./stripeConnectLaunch";
import { installInPersonPay } from "./inPersonPay";
import { installListingLikes } from "./listingLikes";
import { installSavedListings } from "./savedListings";
import { installCardComments } from "./cardComments";
import { installCommentLikes } from "./commentLikes";
import { installAccountSettings } from "./accountSettings";
import { installListingEdit } from "./listingEdit";
import { installWizardEdit } from "./wizardEdit";
import { installCollectionRemove } from "./collectionRemove";
import { installProfileEdit } from "./profileEdit";
import { installFollowUser } from "./followUser";
import { installCollectionReorder } from "./collectionReorder";
import { installListingRouteFallback, checkListingRouteNow } from "./listingRouteFallback";
import { installRatingFixups } from "./ratingFixups";
import { installProfileFixups } from "./profileFixups";
import { installDemoTextFixups } from "./demoTextFixups";
import { versioned } from "./webAssetsVersion";

export const WEB_INSTALL_PATH = "/web";
// Part 1 (owner: "move it to /"): the demo now owns the site root too. Kept
// as its own constant, not folded into isWebInstallPath's body, so the two
// places that need to agree on "which routes does the install own" — here
// and App.tsx's Router() — read off the SAME exported values instead of two
// hand-copied conditions silently drifting apart (exactly the failure mode
// SCOPE_CLASS's own comment already warns about, one export down).
export const WEB_ROOT_PATH = "/";

/** True on both the real mount ("/") and its alias (WEB_INSTALL_PATH, kept
 *  working so existing links/probes don't break). Exported so App.tsx's
 *  Router() and this file's own isActive check can never disagree about
 *  which routes the install owns. */
export function isWebInstallPath(location: string): boolean {
  return location === WEB_ROOT_PATH || location === WEB_INSTALL_PATH || location.startsWith(WEB_INSTALL_PATH + "/");
}

const DATA_SRC = versioned("/web/data.js");
const APP_SRC = versioned("/web/app.js");
// Generated by scripts/scope-web-demo-css.mjs from Blake's styles.css — see
// that script for what it does and why it's generated rather than hand-kept.
const STYLE_HREF = versioned("/web/styles.scoped.css");
// Hand-written, NOT generated — see that file's own header for why it's kept
// separate from the generator's output. Loaded second so it wins the cascade.
const OVERRIDES_HREF = versioned("/web/overrides.css");

// Exported so a test can assert this matches scripts/scope-web-demo-css.mjs's
// own SCOPE_CLASS (with its leading "." stripped) rather than the two
// constants silently drifting apart — recon 16, L6 "related" note: renaming
// either one, uncaught, ships a completely unstyled (and un-suppressed-demo-
// bar) mount with every existing test green.
export const SCOPE_CLASS = "blake-install-scope";

let bootStarted = false;

// Latent defect found while reading this file (recon report, 2026-08-24):
// loadScript had no idempotency check, so the documented retry path —
// `bootStarted = false` in the outer .catch() below — re-injects
// <script src="/web/data.js"> after a LATER failure (e.g. installFloorListings'
// own network/count check), even though data.js's top-level `const
// LISTINGS`/`USERS` are already declared in this tab's global scope from the
// FIRST injection. A second classic <script> redeclaring the same top-level
// `const` is a global SyntaxError — and per the onload/onerror comment just
// below, a parse/runtime error inside a classic script still fires the
// element's `load` event (only a network failure fires `error`), so the
// promise resolves anyway and boot continues on a half-broken global scope,
// with nothing but an unread console error to show for it.
//
// CACHE THE PROMISE, NOT JUST "IS THE TAG ALREADY THERE": caching only a
// boolean/element-exists check would let a second caller for the same src
// resolve immediately — before the FIRST injection has actually finished
// loading — which is a different bug (a caller racing ahead of code that
// still needs that script's globals to exist). Caching the Promise itself,
// in flight or already settled, means every caller for the same src awaits
// the one real injection, whichever call started it.
//
// A REJECTED promise is deliberately NOT cached forever — it's removed from
// the map in that case. A failed load (onerror; network only) means nothing
// was declared, so it's both safe and necessary (for the retry path this
// fixes) to let the next call actually try loading it again.
const scriptLoadPromises = new Map<string, Promise<void>>();

export function loadScript(src: string): Promise<void> {
  const existing = scriptLoadPromises.get(src);
  if (existing) return existing;

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = false; // preserve load order relative to sibling scripts
    script.setAttribute("data-web-install", "1");
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`failed to load ${src}`));
    document.body.appendChild(script);
  });
  scriptLoadPromises.set(src, promise);
  promise.catch(() => {
    scriptLoadPromises.delete(src);
  });
  return promise;
}

function loadStylesheet(href: string): Promise<void> {
  /* The page chrome on About, Help, and the other pages outside the web app
     loads these same two sheets (components/layout.tsx). Reuse one that already
     loaded; anything else still gets a fresh tag, so a failed or pending load
     can never leave the boot waiting. */
  const existing = document.querySelector<HTMLLinkElement>(`link[rel="stylesheet"][href="${href}"]`);
  if (existing?.sheet) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.setAttribute("data-web-install", "1");
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`failed to load ${href}`));
    document.head.appendChild(link);
  });
}

export function WebInstallHost() {
  const [location] = useLocation();
  const { authFetch, betaEnded, user, loading, isAdmin, signOut } = useAuth();
  // recon 16: WebInstallHost is a sibling of Router() in App(), so Router()
  // returning <BetaEndedScreen> for betaEnded does not stop this component
  // from booting and showing Blake's full floor underneath it. Consult the
  // same flag here so a beta-ended visitor gets neither the boot nor the
  // visible mount, not just a Router-side interstitial with a live page
  // rendering behind it.
  const isActive = !betaEnded && isWebInstallPath(location);
  const containerRef = useRef<HTMLDivElement>(null);
  // #app itself — observed directly (not re-queried by id) so the mode-mirror
  // effect below has a stable reference independent of DOM lookups.
  const appRef = useRef<HTMLDivElement>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  /* Set once app.js is loaded and its seams are being installed, so the
     flag-driven scan effect below knows there is something to install into. */
  const [booted, setBooted] = useState(false);

  // SIGN OUT (card 1217901370191010) — wired UNCONDITIONALLY, independent of
  // isActive/boot success/failure: a visitor must be able to actually sign
  // out even if the floor never finished booting, and this component mounts
  // once at the app root for the life of the tab (this file's own header
  // comment), so there is no "not mounted yet" race with app.js's click
  // handler — by the time a visitor could click [data-acctsignout], this
  // effect has already run. See signOutAndReload's own doc comment for why
  // this calls the REAL AuthContext signOut rather than a second POST.
  useEffect(() => {
    (window as unknown as { __gbzSignOut?: () => Promise<void> }).__gbzSignOut = () =>
      signOutAndReload(window, signOut);
    return () => {
      delete (window as unknown as { __gbzSignOut?: unknown }).__gbzSignOut;
    };
  }, [signOut]);

  // Read by the cross-route guard below, which is registered ONCE and must
  // see the CURRENT route rather than the one that was live when it was
  // bound.
  const isActiveRef = useRef(isActive);
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);
  // Read by the bridge (floorTransport's __gbzRemoveCollectionRow): a remove
  // whose answer lands while the visitor is off /web must not write #/profile
  // onto that page's URL. The guard below swallows the hashchange there, so
  // nothing would repaint, /help#/profile would sit in history, and the
  // landing's message would be lost, or said only on a later return to
  // #/profile. Published once; it reads the ref, never a closure.
  useEffect(() => {
    (window as unknown as { __gbzWebActive?: () => boolean }).__gbzWebActive = () => isActiveRef.current;
  }, []);

  // CROSS-ROUTE LISTENER GUARD (recon 21, V5-2). Blake binds two handlers to
  // shared globals and never unbinds them — app.js:5300 `keydown` on
  // `document` for the "/" key, app.js:5310 `hashchange` on `window` calling
  // his render(). His bundle is loaded once, for the life of the tab, so
  // after ONE visit to /web those two stay live on /help, /login, /admin and
  // every marketing page: pressing "/" swallows the keystroke, appends a junk
  // #/discover fragment, repaints #app (his render() re-renders unconditionally
  // on any hashchange) and jumps scroll to top. His files are read-only, so
  // the only place this can be stopped is here.
  //
  // (v44 additionally listed "repaints the whole site's theme" here — his old
  // render() wrote document.documentElement.dataset.mode on every call, which
  // this same off-route repaint would have triggered. v47 deleted that write
  // — see the WEBSITE-WIDE THEME MIRROR block below for what render() writes
  // instead and why this guard's other three effects are unchanged.)
  //
  // Registered in an effect declared BEFORE the boot effect, so it is bound
  // long before app.js is injected — and in the CAPTURE phase, so it does not
  // depend on that ordering holding.
  //
  // NOT a blanket "stop every keydown while off-route": the app binds its own
  // window keydown handlers (layout.tsx:83 and landing.tsx:81 for Escape,
  // TutorialModal.tsx:66, three admin tabs) and killing those would trade one
  // bug for a worse one. The keydown
  // arm therefore stops exactly the event Blake's handler acts on and nothing
  // else — verified: no component in this app handles a bare "/". The
  // hashchange arm needs no such narrowing: the whole SPA contains one
  // in-page anchor (admin/DashboardsTab.tsx:798) and the only other
  // hashchange listener (legal/LegalDocumentView.tsx:227) also runs its
  // scroll on mount, which this cannot affect.
  useEffect(() => {
    const guard = (event: Event) => {
      if (isActiveRef.current) return;
      if (event.type === "keydown") {
        const e = event as KeyboardEvent;
        if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
        const a = document.activeElement;
        if (a && (a.tagName === "INPUT" || a.tagName === "TEXTAREA" || (a as HTMLElement).isContentEditable)) return;
      }
      event.stopImmediatePropagation();
    };
    window.addEventListener("keydown", guard, true);
    window.addEventListener("hashchange", guard, true);
    return () => {
      window.removeEventListener("keydown", guard, true);
      window.removeEventListener("hashchange", guard, true);
    };
  }, []);

  // THE LISTING ROUTE FALLBACK. `#/listing/<id>` resolves only against
  // LISTINGS, which holds ACTIVE rows only — so a collection item that sold, a
  // notification about a listing that just locked, and every deal item on an
  // accepted trade all navigate to a route his router cannot resolve, and it
  // drops them on the home feed without a word. See listingRouteFallback.ts
  // for the full chain and for the ten places his own code already checks this.
  //
  // ⚠️ THE POSITION OF THIS EFFECT IS LOAD-BEARING, at both ends.
  //   · AFTER the cross-route guard above, which must keep winning: a stale
  //     listing hash changing while the visitor is on /help has to navigate
  //     nobody anywhere, and that guard's stopImmediatePropagation is what
  //     guarantees it.
  //   · BEFORE the boot effect below, because hashchange is dispatched AT
  //     window, where capture and bubble listeners alike run in REGISTRATION
  //     order — a listener added after app.js's own
  //     `window.addEventListener("hashchange", render)` (app.js:5344) could not
  //     stop his render, and the visitor would watch the home feed paint on the
  //     way out. Registering here means we are bound long before app.js is
  //     injected, exactly as the guard above is.
  //
  // 🪤 authFetch goes through a REF, not the closure. This effect must keep its
  // empty deps — re-running it would unbind and rebind the listener AFTER
  // app.js is injected, losing the registration order the paragraph above
  // depends on. But the fetch itself has to use the CURRENT authFetch: a
  // visitor who signs in mid-session would otherwise be served by the identity
  // captured at first render, which is the staleness this file already
  // documents for the boot.
  /* ── THE SCAN FLAG, READ WITHOUT react-query ────────────────────────────
     useFeatureFlags() would have been the obvious call and it was the first
     thing I wrote. It is a useQuery, and adding it here made WebInstallHost
     require a QueryClientProvider — which crashed FOUR existing suites that
     render this component in isolation with "No QueryClient set". Production
     has a provider, so it would have shipped; the tests were the signal, and
     the failure mode they exposed is the reason not to do it. Coupling the
     boot of the entire web floor to a data library for ONE BOOLEAN means a
     missing provider takes the whole floor down rather than one feature, and
     every future test that renders this has to know.

     So: a plain fetch, and the flag module's own exported pure functions.
     resolveFlags is the same code useFeatureFlags calls, so rollout_pct and
     the anonymous bucket behave identically — what is skipped is the caching
     and de-duplication, which for one request at boot is not worth a
     dependency.

     Fails closed twice over: the state starts false, and a non-ok response or
     a throw leaves it false. The seam is therefore never installed on the
     strength of a flag nobody actually read. */
  const [scanEnabled, setScanEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/feature-flags");
        if (!res.ok) return;
        const body = (await res.json()) as { flags?: { key: string; value: boolean; rollout_pct?: number | null }[] };
        const flags = resolveFlags(body.flags ?? [], user?.id || ANONYMOUS_ROLLOUT_ID);
        if (!cancelled) setScanEnabled(flags.scan_enabled === true);
      } catch {
        /* Denied by omission — the state stays false. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const authFetchRef = useRef(authFetch);
  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);
  useEffect(
    () => installListingRouteFallback(window, (input, init) => authFetchRef.current(input, init)),
    [],
  );

  /* ── THE COVER-PHOTO SCAN, INSTALLED AND REMOVED BY ITS FLAG ─────────────
     Until 2026-09-14 this was one unconditional line inside the boot, and the
     web scan was the only surface of the feature with no gate on any layer.

     Returning the installer's teardown is the whole point: React runs it when
     `scanEnabled` goes false, which DELETES the __gbzWizardScan seams, which
     makes app.js stop rendering the entry card and the banner on the next
     repaint. So an admin flipping the flag off removes the button from a page
     that is already open, within one flag-cache TTL, without a deploy.

     ⚠️ THIS IS THE CANARY, NOT THE ENFORCEMENT. useFeatureFlags combines the
     master switch with rollout_pct for THIS user, so it decides who is shown
     the feature. What stops the spending is requireFeatureFlag on
     POST /api/ai/scan-photo, which no client can talk its way past.

     Fails closed while the flags are in flight: useFeatureFlags returns
     DEFAULT_FLAGS (all false) until the fetch resolves, so the seam appears a
     moment after boot rather than existing before anyone has said it may. */
  useEffect(() => {
    if (!booted || !scanEnabled) return;
    return installWizardScan(window, (input, init) => authFetchRef.current(input, init));
  }, [booted, scanEnabled]);


  useEffect(() => {
    // WAIT FOR AUTH TO SETTLE (R23 cold-load identity). `loading` gates the
    // boot until AuthContext's async `GET /api/auth/me` has resolved, so an
    // already-signed-in visitor's one-shot boot reads the REAL user rather than
    // the null it sees on the first render (isActive is true immediately, but
    // `user` is not populated until auth settles). A guest has loading=false at
    // the first render already, so this does not defer their boot. The check
    // sits BEFORE `bootStarted = true` so a loading-gated early return never
    // consumes the one-shot flag — the boot proceeds once loading clears.
    if (!isActive || bootStarted || loading) return;
    bootStarted = true;
    setBootError(null);

    (async () => {
      await loadStylesheet(STYLE_HREF);
      await loadStylesheet(OVERRIDES_HREF);

      await loadScript(DATA_SRC); // declares LISTINGS/USERS/etc.

      // The bridge: an INLINE classic script (never a module), so its body
      // shares the global lexical scope data.js's `const`s live in — see
      // floorTransport.ts's top comment for why an ES module can't reach
      // `LISTINGS` by name directly.
      const bridge = document.createElement("script");
      bridge.textContent = bridgeScriptSource;
      bridge.setAttribute("data-web-install", "1");
      document.body.appendChild(bridge);
      // A parse/runtime error inside a classic <script> still fires `load`
      // on the element (recon 16, L5) — only a network failure fires
      // `error`. Trusting `onload` alone is silent about everything else, so
      // probe for a symbol the script is supposed to define instead of
      // trusting the event. Only __gbzInstallFloor is checked here — the
      // load-bearing one, whose absence must abort the whole mount (see the
      // FAILS CLOSED note below). Its SIBLING assignments in this same script
      // — __gbzMergeListing, __gbzSetListingComments and __gbzMergeUsers —
      // back the two enhancement features below (real search, real comments,
      // and the comment AUTHORS that second one now also installs into USERS);
      // each has its own existence check and its own non-fatal try/catch at
      // its call site, on purpose — see the comment there for why they must
      // NOT abort the whole boot the way this one does.
      if (typeof (window as unknown as { __gbzInstallFloor?: unknown }).__gbzInstallFloor !== "function") {
        throw new Error("bridge script did not define window.__gbzInstallFloor");
      }

      // FAILS CLOSED (recon 16, L3-1 + L4-7 + L4-8): installFloorListings
      // throws — never calls the bridge — on a fetch failure or too few
      // real listings (see floorTransport's MIN_LISTINGS_TO_INSTALL). That
      // throw propagates out of this whole async function, so app.js below
      // is never loaded and Blake's fabricated demo data never has a chance
      // to render as if it were the real marketplace. There is deliberately
      // no local try/catch here any more — falling open to "demo data
      // stands" was the bug.
      //
      // user?.username feeds floorTransport's __gbzMe (store.me), and
      // store.authed is now `__gbzMe !== null` — so this value's TIMING is
      // load-bearing, not cosmetic. The boot effect is gated on auth's
      // `loading` and re-runs when it clears (see the gate and dep array), so
      // on a cold load for an already-signed-in visitor this reads the REAL
      // handle: AuthContext.init() sets user/token BEFORE it clears loading, so
      // the render that lifts the gate already carries the populated user and
      // an authFetch bearing the token.
      //
      // KNOWN LIMITATION (documented follow-up, not this fix): the boot is
      // still a one-shot for the life of the tab (bootStarted above), so a
      // visitor who signs in MID-SESSION — after a guest boot has already run —
      // keeps __gbzMe null until a reload. Making that reactive is a separate
      // card; floorTransport marks store.me/store.authed configurable precisely
      // so a later pass can redefine them without touching this boot.
      //
      // isAdmin (card 1217901328215272 — "no admin nav") rides the SAME call,
      // the same reason username does: it feeds store.isAdmin, a THIRD
      // identity accessor pinned alongside store.me/store.authed inside the
      // SAME __gbzInstallFloor call, at the SAME boot-time-only cadence — a
      // mid-session promotion to admin is exactly the "KNOWN LIMITATION"
      // immediately above, not a new gap this line introduces.
      const { count, userIds } = await installFloorListings(authFetch, user?.username ?? null, isAdmin);
      console.info(`[web-install] floor: installed ${count} real listing(s)`);

      // THE DEEP-LINK HALF of the listing route fallback, and it needs its own
      // call: a link shared as /web#/listing/<id> sets the hash before anything
      // runs, so no hashchange ever fires for it and the listener above never
      // sees it. This is the first moment the question can be answered — the
      // floor has just landed, and app.js has not painted yet — so a shared
      // link to a sold item leaves for the real page without the home feed
      // being drawn at all. Sharing a listing link is the entire reason
      // pages/listing-landing.tsx exists.
      if (checkListingRouteNow(window)) {
        console.info("[web-install] listing route: not on the floor, opening the real page");
        return;
      }

      await loadScript(APP_SRC); // calls render() synchronously at EOF
      // Same onload-lies-on-parse-error problem as the bridge, for Blake's
      // own file this time: confirm something actually painted into #app
      // rather than trusting the script tag's `load` event.
      const appRoot = document.getElementById("app");
      if (!appRoot || appRoot.children.length === 0) {
        throw new Error("app.js loaded but did not render into #app (possible parse/runtime error)");
      }

      /* 🔑 HERE, AND NOT ONE LINE EARLIER. This clears the registration chips
         Blake ships pre-selected, and the bindings it touches are declared by
         app.js, which has only just loaded on the line above.

         The first version of this fix lived inside __gbzInstallFloor (:366),
         twenty lines before app.js existed. It swallowed the ReferenceError
         and shipped to prod as a no-op on 2026-09-01. The position of this
         call IS the fix; the code it calls barely changed.

         Not fatal if it fails: a pre-selected chip is a bad default, not a
         broken floor. But it is LOGGED, never silent, because the last version
         failing silently is exactly how it reached production. */
      const prefill = resetAuthPrefill(window);
      if (!prefill.reset) {
        console.warn(`[web-install] auth prefill NOT reset: ${prefill.reason ?? "unknown"}`);
      }

      // blockAuthRoute (recon 23, L5) RETIRED 2026-08-31: #/auth is real now —
      // the wizard submits through the __gbzAuth seam (authSeam.tsx) and
      // /login + /register redirect INTO it, so guests must be able to stand
      // there. route()'s own authed check still bounces signed-in visitors.

      // PART 2, recon 19 §1b: DISC_TAGS/ACCOUNT are declared INSIDE app.js,
      // not data.js, so they don't exist until app.js has loaded — this can
      // only run here, after the check above confirms app.js actually
      // executed. Home (the view app.js just painted) reads neither of them,
      // so nothing sample-shaped has been painted yet;
      // see floorTransport.ts's appFixtureStripSource for why that ordering
      // is safe.
      const appBridge = document.createElement("script");
      appBridge.textContent = appFixtureStripSource;
      appBridge.setAttribute("data-web-install", "1");
      document.body.appendChild(appBridge);
      const stripAppFixtures = (window as unknown as { __gbzStripAppFixtures?: () => void }).__gbzStripAppFixtures;
      if (typeof stripAppFixtures !== "function") {
        throw new Error("app-fixture bridge script did not define window.__gbzStripAppFixtures");
      }
      stripAppFixtures();

      // Owner instruction, 2026-08-23 ("point all the footer and buttons
      // correctly and the sign-in button, do it all"): his internal-only
      // #/... link graph gets rewritten to real destinations here, after
      // app.js's EOF render() has produced the DOM this fixes and before
      // anything else can navigate. Installs a MutationObserver so the fix
      // survives every later render() (see linkFixups.ts's own header for
      // why that observer, not a wrapped render(), is what actually covers
      // every one of his 165 rerenderKeepScroll/render() call sites).
      installLinkFixups(window);

      // A profile photo that will not load becomes the person's initials, not
      // the browser's broken-image glyph (card 1218624237210752). Same place
      // and posture as the link fixups: after app.js's first paint, and an
      // observer so it survives every later one. See avatarFallback.ts.
      installAvatarFallback(window);

      // THE WRITE GUARD — suppress Blake's demo write handlers, which mutate our
      // real rows client-side, make no server call, and toast as though the
      // action succeeded (Asana 1217799695855666, and 1217805587637546 for the
      // pay button specifically).
      //
      // ⚠️ Installed OUTSIDE the `user?.id` block below, deliberately. It is a
      // suppression, not a data install: there is nothing to fetch, nothing to
      // 401 on, and a guest who somehow reaches a guarded control should be
      // stopped by the same code path as a signed-in visitor. Guarding fewer
      // people than necessary is the only way this can fail badly.
      //
      // 🪤 Registered BEFORE installDealThreadLoader's own capture listener, and
      // it uses stopPropagation rather than stopImmediatePropagation precisely
      // so that ordering cannot matter — see writeGuard.ts's header.
      //
      // 🔴 AN ENTRY DOES **NOT** LEAVE GUARDED_WRITES WHEN ITS HANDLER BECOMES
      // REAL, and this comment said the opposite until 2026-09-28. Following it
      // would undo the work it describes, because option A — Blake rewiring his
      // handlers to real endpoints — never happened. What happened instead is
      // that OUR layer wired the real write while HIS handler stayed in the
      // bundle underneath it, so both are live on the same click. The entry
      // stays for two reasons, both measured rather than argued in
      // writeGuard.ts's header:
      //
      //   1. His handler claims the outcome SYNCHRONOUSLY, before any request
      //      can resolve. An offer the server refuses has already been priced.
      //   2. It is the fail-safe. If the real module throws or fails to
      //      install, the control goes back to being DEAD rather than to lying.
      //
      // Selectors are real AND still guarded on that basis (writeGuard.ts's header
      // names them). Two entries have ever left the array: #promo-start, for
      // which writeGuard.ts states the two structural properties that made it
      // removable — neither of which the others have — and [data-owndelete],
      // which left with its control (the ⋯ "Delete listing" itself was deleted,
      // 2026-09-28), not because it became real. 🔑 Read that header before
      // removing anything; the argument lives there, and this is a pointer to
      // it, never a second source.
      //
      // Found by Tyler's virtual-user pass (card 1218908858772303): with both
      // lanes wiring controls this week, this comment was the likeliest way an
      // entry would silently leave the list.
      installWriteGuard(window);

      // THE WIZARD PUBLISH, registered immediately AFTER the write guard and
      // deliberately not replacing it. The guard uses stopPropagation, never
      // stopImmediate, so both capture listeners receive the click — which
      // means if anything in here throws, #wz-publish stays dead rather than
      // falling back to fabricating a listing. See wizardPublish.ts.
      installWizardPublish(window, authFetch);

      // THE COVER-PHOTO SCAN, registered immediately after publish because it
      // BORROWS publish's uploader (__gbzUploadWizardPhoto) so the cover photo
      // crosses the wire once for the scan and the listing both. The borrow is
      // resolved when a scan RUNS, not when this installs, so the order is for
      // the reader rather than for correctness — but a scan installed without
      // publish would have nothing to upload with, and these two belong
      // together for that reason.
      //
      // 🔑 THIS LINE IS SLICE 4. Slices 2 and 3 built the entry card, the
      // question, the installer and the whole progress banner and deliberately
      // left this call out: app.js gates every piece of that UI on
      // window.__gbzWizardScan existing, so until this line the feature was
      // invisible on the live site and on the standalone demo alike. It lands
      // now because the Details step can finally keep the promise the banner
      // makes — "you'll confirm them on the next step" — rather than sending a
      // seller to a step with nothing on it.
      /* 🔑 THE SCAN SEAM IS NO LONGER INSTALLED HERE. It moved to its own
         effect below, keyed on `scan_enabled`, because this boot is a ONE-SHOT
         (`bootStarted`) and a gate evaluated once at boot is not a gate: an
         admin switching the flag off would not take the button away until the
         seller reloaded, and a flag that resolved AFTER boot — the ordinary
         case, since it is a fetch — would have left the feature dark forever
         on a tab where it should be on.

         app.js gates every piece of the scan UI on `window.__gbzWizardScan`
         existing (scanSeamPresent), so not installing is not a hidden button:
         it is the entry card, the question modal and the progress banner all
         being absent. That mechanism was already there; this change is only
         about who decides. */
      setBooted(true);

      /* THE COMMENT COMPOSERS. Both of Blake's "Add your take" boxes pushed a
         fabricated comment onto a real seller's listing row with no POST, which
         is why writeGuard deadened them, and why the owner reported the result
         as a broken button (2026-08-28). They post for real now.

         AFTER the guard, deliberately, exactly as installWizardPublish above:
         the guard kills the fabricator, this does the real write, and both run
         because the guard uses stopPropagation and not its Immediate cousin. If
         this ever fails to install, the composers fall back to being dead rather
         than to lying. */
      installCommentPost(window, authFetch);

      /* THE PRICE VOTE. His handler wrote store.votes client-side, toasted
         "Vote counted" over no server call, and REFUSED a second click with
         "You already called this one" — so a mistap was permanent. Owner asked
         for click and unclick (2026-08-28); this does the real POST/DELETE.

         After the guard, which now covers [data-vote] for a reason the other
         entries do not share: his toast is synchronous, so letting it run would
         announce a vote before the server had accepted one. */
      installListingVotes(window, authFetch);

      // THE HEART, real as of #723's hook. No GUARDED_WRITES entry: Blake
      // shipped the attribute with NO handler, so there is no fabricator to
      // suppress — the control was inert, not lying.
      //
      // ⚠️ This one DOES stop propagation, unlike its neighbours. His card-open
      // handler excludes eight selectors and [data-likelisting] is not among
      // them, so a tap on the heart would otherwise navigate to the detail page
      // — the thing his own ruling forbids. Same posture as installAccountActions:
      // deliberately unguarded control, so we own the suppression.
      installListingLikes(window, authFetch, { signedIn: !!user?.username });

      /* MY SAVED LISTINGS (card 1218241608398396). Wires the save tag to the
         same GET/POST /api/favorites mobile uses, so both share one saved
         set. `[data-savelisting]` moved from DELIBERATELY_UNGUARDED into
         GUARDED_WRITES in the same change — his own handler stays as the
         fail-safe beneath this, same posture as installListingLikes above
         and [data-follow]'s entry in writeGuard.ts. See savedListings.ts. */
      installSavedListings(window, authFetch, { signedIn: !!user?.username });

      /* THE COMMENT HEART (Blake 2026-09-10: tappable, red when liked). A real vote
         by comment id on every heart that carries one; the local fake on the same
         element is in GUARDED_WRITES above, so a tap counts once. See commentLikes.ts. */
      installCommentLikes(window, authFetch);

      /* THE ACCOUNT FORM. #acct-save toasted "Saved" and #acct-pass toasted
         "Password reset email sent", both over zero calls. Six fields now PATCH
         for real, the address PUTs to its own route, and the reset email is
         actually sent. Both stay guarded; this runs after. */
      installAccountSettings(window, authFetch);

      /* EDITING A LISTING. Reported 2026-08-31 by a real person: post in the
         app, open the listing on mobile web, change details, tap Save Changes —
         nothing happens at all. #editl-save mutates LISTINGS in memory and
         toasts "Saved · your changes are live" over zero calls, so it sits in
         GUARDED_WRITES and the guard was suppressing it. Correctly — the guard
         turned a lie into a dead end. This is the real write that was missing.
         Installs in CAPTURE so the guard's stopPropagation cannot eat the click,
         and #editl-save STAYS guarded as the fail-safe. See listingEdit.ts. */
      installListingEdit(window, authFetch);

      /* THE FULL EDIT'S SEED. The collection's Edit and Quick Edit's More
         Details open the wizard on a draft the page's row can only half fill;
         this reads the record from the server and completes it, and the
         publish interceptor above saves an edit through PATCH, never a create
         (card 1217916966128774). See wizardEdit.ts. */
      installWizardEdit(window, authFetch);
      // The want pop-up's Save and the profile's ✕ write for real (PR B, card 1218637972579066
      // item 4); the guard above suppresses the page's own push and splice for everyone, so this
      // installs for everyone too, here rather than after the authed awaits: the tour's guest
      // fixtures keep their ✕ (no id: dropped locally), and a signed-in seller's Save is never dead
      // while the wallet and deals reads are still out.
      installWantsWrite(window, authFetch);

      /* REMOVE, ON A PIECE IN YOUR COLLECTION. It lied with nothing in front
         of it: two taps, then "Removed from your collection" over zero calls,
         and the piece back on the next load (card 1218907631345203). Its
         "remove:" branch is in GUARDED_BRANCHES as the fail-safe; this sends the
         real DELETE the way the app does. Its own try/catch: if it throws,
         Remove falls back to dead rather than to the fabricated success. See
         collectionRemove.ts. */
      try {
        installCollectionRemove(window, authFetch);
      } catch (err) {
        console.error("[web-install] collection remove not installed:", err);
      }

      /* THE PROFILE MODAL. #edit-save read the display name and handle only to
         VALIDATE them, then threw both away, and toasted "Profile updated" over
         zero calls (Blake, 2026-08-31: "I tried to change my username and noticed
         it doesn't save"). Worse than the listing bug it sits beside: that one
         was at least GUARDED, this was in neither list and lied with nothing in
         the way. Now guarded AND wired. Capture, same as above. */
      installProfileEdit(window, authFetch);

      // 🔴 RETIRED 2026-08-28. "+ List an item" used to be redirected OUT of the
      // marketplace to /create-listing, because the wizard it leads to ended at
      // a #wz-publish that fabricated a listing client-side. Correct then, wrong
      // now: the wizard publishes for real (wizardPublish.ts, both branches),
      // and its step-1 subcategory gate stops a seller reaching the last step
      // with a draft the listing route would refuse. So the entry stays inside
      // the marketplace, which is where the owner asked for it.
      //
      // The module and its tests are KEPT rather than deleted: it is the
      // documented fallback if the publish wiring is ever reverted, and its
      // header records why the two controls it never covered were left alone.
      //
      // 🪤 [data-ownedit] was always excluded there because it sends an EDIT
      // intent through this same #/list route. That hazard did not go away with
      // the redirect, it MOVED: wizardPublish sends a draft carrying
      // editMoreReturn through the update routes (saveEdit), never the create,
      // and editMoreReturn is the only thing in the runtime that knows the
      // difference between editing and creating.

      // The "Check out" thread CTA. Its sibling CTAs already answer honestly
      // ("… · opens in production"); this one navigates into a checkout that
      // cannot render, because the accept handler that would have set t.fees
      // is itself guarded — so the visitor lands back on the home feed with no
      // explanation. Reported by Tyler 2026-08-27. Deliberately NOT a
      // GUARDED_WRITES entry: one handler serves every thread CTA, so guarding
      // the selector would break the three that work. See checkoutCtaGuard.ts.
      installCheckoutCtaGuard(window);

      // A trade has two payers and the screen only ever knew about one. The
      // server has published `myCheckoutComplete` on the checkout summary all
      // along (offers.ts:2191); nothing on the web read it, so a party who had
      // already paid came back to "Complete Checkout · $265" — a figure they
      // did not owe. The click was already dead (writeGuard's GUARDED_WRITES[0])
      // but the STATEMENT was not, and the statement is the false part.
      //
      // Installed AFTER the CTA guard above and it does not matter: that one
      // intercepts clicks on the thread rail, this one watches the checkout
      // screen's own paint. They touch different controls.
      installCheckoutContext(window);

      // THE TWO ACCOUNT CONTROLS. "Export my data" gets wired to the real
      // GDPR/CCPA endpoint; "Delete my account" routes to the only correct
      // implementation of it in the repo, because its copy contradicts the
      // endpoint and its markup cannot hold that flow's confirmation field.
      // See accountActions.ts — both were toasting a filed request that had
      // never been filed.
      installAccountActions(window, authFetch);

      // "Connect with Stripe" on the wallet — the real onboarding hand-off
      // (card 1217981132152628). Replaces the writeGuard suppression of
      // #wal-connect: same capture-phase intercept that kept Blake's
      // fabricated "verified · payouts on" dead, now running the actual
      // create → onboarding-link → redirect chain register.tsx uses, ?ctx=web
      // included. The uninstall return is deliberately ignored — the listener
      // lives as long as the tab, like every install above.
      installStripeConnectLaunch(window, authFetch);

      // Task brief, same evening: retiring the last of the fabricated
      // numbers on this surface, now that the ratings system exists to make
      // fabrication #1 (the hardcoded "★ 4.9") retirable for real rather
      // than deletable. Same observer-on-#app pattern as installLinkFixups
      // above, in their own modules — see ratingFixups.ts and
      // demoTextFixups.ts for why each is separate from linkFixups.ts
      // (off-limits tonight: merged/reviewed) and from each other (one needs
      // a network round trip per profile view, the other is pure DOM text).
      installRatingFixups(window, authFetch, userIds);

      // Card 1218200003007957, same evening: the public profile's followers/
      // following counts, bio, and Active-Items/Live-Listings set were all
      // floor-local scraps (mapSellerToUser hardcodes the first two; the
      // third only ever saw whatever fraction of a seller's listings the
      // boot-time 100-row snapshot happened to include; the floor loads every
      // active listing since 2026-09-27). Same slot as
      // installRatingFixups directly above and the same SHAPE of fix — see
      // profileFixups.ts's own header for why it is a separate module
      // rather than a third branch on that one, and for the MANDATORY
      // companion this ships with (demoTextFixups.ts's
      // neutralizePublicFollowerTriggers, already wired into
      // installDemoTextFixups below): the followers/following modal's
      // fabricated roster was only ever suppressed while the count read
      // zero, and this call is what makes it stop reading zero.
      /* selfUserId — the signed-in user’s own id, off the SAME boot that
         supplies user?.username above. Without it the self-view branch of
         findProfileTile cannot resolve an id: `userIds` is built from the
         boot-time floor snapshot, so it contains this user only if they
         happen to be a seller in it, and the fix would silently do nothing
         for everyone else. */
      installProfileFixups(window, authFetch, userIds, user?.id ?? null);

      /* THE FOLLOW BUTTON. Takes the SAME userIds object ratingFixups holds,
         by reference and not a copy: installLiveListingComments mutates it as
         comment authors arrive, so a profile reached by tapping a commenter
         resolves even though that handle was not on the floor at boot.

         His endpoints are keyed by id and his markup carries a handle, with no
         by-username route to bridge them — so this map IS the resolution, and
         a handle missing from it is refused out loud rather than silently. */
      installFollowUser(window, authFetch, userIds);
      // The drag-to-reorder save. Owns the HTTP for Blake's grid; his own
      // handler stays in charge of the optimistic move and hands the undo in.
      installCollectionReorder(window, authFetch);
      installDemoTextFixups(window);

      // THE PRICE CHECK'S RUN BUTTON. Same posture as the write guard above:
      // it is a suppression of a false claim before it is a feature, and it
      // must cover a guest exactly as it covers a signed-in visitor — the
      // panel Blake fabricates is guest-open on all 101 real listings and
      // needs no auth to reach.
      //
      // 🔴 DELIBERATELY NOT in the lenient try/catch below with the discover
      // and comments installs. Those two upgrade a surface that is ALREADY
      // real, so a throw there costs an enhancement. A throw HERE would leave
      // app.js:4308's 750ms fake wired to app.js:825's hand-written
      // "HIGH CONFIDENCE / PRICED FAIR / Comparable pieces cleared between …"
      // over every real listing on the floor. Falling open to a fabrication is
      // the failure mode this whole boot sequence is fail-closed against, so
      // this one throws into the same abort path as installFloorListings.
      //
      // ⚠️ ORDERING CONSTRAINT — THIS CALL MUST STAY BELOW THE FIXUPS ABOVE.
      // It used to run before them, and that was wrong: unlike installFloor-
      // Listings, a throw from here does NOT fail closed. app.js has already
      // painted by this point and the boot's error renders BESIDE #app, which
      // is never cleared — so the abort leaves Blake's demo on screen and
      // takes every installer after it down with it. From its old slot above
      // installRatingFixups, a throw also took those two — the ratings and the
      // demo-text fixups, both guest-facing honesty repairs. From here it
      // costs only its own suppression and the ENHANCEMENTS underneath (the two
      // try/caught installs and the authed wallet/deals block), which is the
      // right thing to lose. Nothing above reads anything this writes, so the
      // move is free.
      installPriceCheckRun(window, authFetch);

      // THE WIZARD'S TWIN of the same fabrication (F6). Same posture, same
      // slot, same reasons as the call directly above — and it is the same
      // defect one screen earlier: app.js:1613 builds the "market" band as
      // ±15% of the seller's own typed number and prints HIGH CONFIDENCE /
      // PRICED FAIR / "Comparable pieces cleared between $X and $Y in the last
      // 90 days" over it.
      //
      // 🔴 NOT in the lenient try/catch below, for installPriceCheckRun's
      // exact reason: a throw here would leave app.js:4871's 750ms fake wired
      // to a verdict that is arithmetically incapable of disagreeing with the
      // seller. Falling open to a fabrication is what this boot sequence is
      // fail-closed against.
      //
      // ⚠️ BELOW THE FIXUPS, like the call above and by the same rule (F5):
      // app.js has already painted by here, so a throw does NOT fail closed —
      // it leaves Blake's demo on screen and takes every installer after it
      // down. From this slot it costs only its own suppression and the
      // enhancements underneath, never the guest-facing honesty repairs above.
      installWizardPriceCheck(window, authFetch);

      // Task targets #1 and #3: real search for #/discover and real
      // comments for #/listing/:id. Both wrap a top-level app.js `function`
      // (discSearchData / render) the same way the retired blockAuthRoute wrapped route()
      // above, so both need app.js to have already run. Placed after the
      // rating/demo-text fixups above rather than before: those retire
      // fabricated values and are already shipped/reviewed on develop, and
      // neither set of installs reads anything the other writes (rating/
      // demo-text patch DOM via their own MutationObservers; discover/
      // comments wrap app.js functions), so there's no dependency in either
      // direction — this ordering just keeps the already-reviewed pair
      // together and layers the newer feature after it.
      //
      // DELIBERATELY NOT fail-closed like the rest of this boot sequence.
      // Everything above this point protects against Blake's FABRICATED
      // demo data rendering as if it were real — that's a correctness
      // failure worth aborting the whole mount for. These two are pure
      // enhancements on top of an ALREADY-real floor: if either throws (app.js
      // stopped defining the function it wraps), the surface it would have
      // upgraded simply keeps today's already-shipped, already-real behavior
      // — Blake's own local search over the installed LISTINGS, or a
      // listing's comments staying at their honest empty default — never
      // fabricated data standing in. So one throwing must not take down the
      // other, or the rest of an otherwise-successful boot.
      try {
        installRealDiscoverSearch(window, authFetch);
      } catch (err) {
        console.error("[web-install] real discover search not installed:", err);
      }
      try {
        installLiveListingComments(window, authFetch, userIds);
      } catch (err) {
        console.error("[web-install] live listing comments not installed:", err);
      }
      /* THE CARD PREVIEWS (Blake 2026-09-09 #2/#3). GET /api/listings carries no
         comments, so every card said "No takes yet" whatever its thread held.
         This fetches a card's thread the first time that card scrolls into view
         and paints that card in place — see cardComments.ts. Same posture as the
         two above: an enhancement on a real floor, never a reason to withhold it. */
      try {
        installCardComments(window, authFetch, userIds);
      } catch (err) {
        console.error("[web-install] card comments not installed:", err);
      }

      // THE WALLET (transactional lane). Same non-fail-closed posture as the
      // two above, and for the same reason: the bridge already installed a
      // fully-shaped zeroed WALLET at boot, so a failure here leaves Blake's
      // honest empty states rather than fabricated money. Never a reason to
      // withhold the floor.
      //
      // Skipped entirely for a guest. Not an optimisation — /api/users/me/*
      // is requireAuth, so a signed-out visitor would spend four round trips
      // collecting four 401s.
      //
      // ⚠️ Reads user?.id, which is a SECOND value off the same one-shot boot
      // that already reads user?.username above. Both share that boot's
      // documented staleness: sign in mid-session and neither lands until a
      // reload. They are read at the same instant from the same object, so
      // they cannot disagree with each other.
      //
      // ⚠️ THIS IS LIVE NOW. It used to read "nothing a visitor can SEE changes
      // while store.authed is pinned false — #/wallet resolves to home" — wired
      // ahead of time so the un-pin would not also be this code's first run.
      // The pin is off (floorTransport.ts:199), so #/wallet resolves and a
      // visitor sees exactly what this installs.
      // LIST & EARN, THE GUEST HALF (card 1218342457183573, item ②).
      //
      // GUESTS ONLY — the inverse of every other promo install in this file,
      // and not a typo. The sheet's own gate is `!store.authed`, so for a
      // signed-in visitor this read would be spent lighting a surface they can
      // never see. It is also the one promo endpoint with no requireAuth: a
      // guest is exactly the population it exists to answer.
      //
      // 🔴 WHY IT IS WIRED AT ALL. #855 shipped the interstitial advertising
      // "your first $25 in fees is on us" to logged-out traffic while consulting
      // NO campaign state, so a guest could take that promise, register, and
      // find every promo surface dark. This call is the whole of the fix.
      //
      // Fail-closed by construction rather than by this catch:
      // installPromoOpenGate touches the bridge ONLY on a 200 carrying exactly
      // {"open":true}, and app.js starts closed, so a throw here lands on the
      // same dark sheet every other non-answer does. Logged, never silent —
      // the failure is invisible on the page by design, which is precisely why
      // it must not also be invisible in the console.
      if (!user?.id) {
        try {
          await installPromoOpenGate(window, authFetch);
        } catch (err) {
          console.error("[web-install] promo open gate not installed:", err);
        }
      }

      if (user?.id) {
        try {
          await installWallet(window, authFetch, user.id);
        } catch (err) {
          console.error("[web-install] wallet not installed:", err);
        }

        // LIST & EARN. Signed-in only for the same reason as the wallet:
        // /api/promo/list-and-earn is requireAuth, so a guest would spend the
        // round trip collecting a 401.
        //
        // 🔴 THIS CALL IS THE ONLY THING THAT MAKES THE PROMO VISIBLE. All four
        // surfaces — the profile chip, the two wallet rows and #/promotion —
        // are gated on PROMO.source === "api", which nothing else sets. So the
        // catch below is not damage control, it is the designed behaviour: an
        // endpoint that is missing, 500s, or answers with a shape we cannot map
        // leaves the promotion unrendered rather than half-rendered. A promo
        // with holes in it is a money claim we cannot honour.
        //
        // Ordered after the wallet because the wallet rows are one of the
        // surfaces this repaints; installing into a wallet that has not
        // painted yet would repaint nothing.
        try {
          await installPromo(window, authFetch);
        } catch (err) {
          console.error("[web-install] promo not installed:", err);
        }

        // THE DEALS LIST. Same posture as the wallet above, and the same
        // signed-in-only gate: /api/deal-threads is requireAuth, so a guest
        // would spend the round trip collecting a 401.
        //
        // Its own try/catch rather than sharing the wallet's: these are
        // independent surfaces, and a wallet that fails to install is not a
        // reason to leave the deals list empty (or the reverse). Blake ships an
        // honest empty state for THREADS, so either failure degrades to that
        // rather than to fabricated rows.
        //
        // ⚠️ Ordered AFTER the wallet deliberately, and DO NOT REORDER IT.
        // installDeals merges deal counterparts into USERS, and renderThread
        // dereferences USERS[t.with] without a guard — so the merge must not
        // race a surface that is already painting.
        //
        // 🔴 This used to add "nothing here is reachable while store.authed is
        // pinned false anyway; this ordering is for when the pin lifts." The
        // pin lifted (floorTransport.ts:199). The ordering is no longer a
        // precaution for a future state — it is the only thing standing between
        // a signed-in visitor on #/deals and a thrown render.
        try {
          const dealCount = await installDeals(window, authFetch, user.id);
          console.info(`[web-install] deals: installed ${dealCount} thread(s)`);
        } catch (err) {
          console.error("[web-install] deals not installed:", err);
        }

        // A refresh on #/checkout/<id> used to land on the home feed: route()
        // resolves it only for a thread with fees in memory, and a fresh load
        // has the list (just installed) but never tapped "summary". Fetches the
        // fees on entry instead. AFTER installDeals, which supplies the rows it
        // looks up, and it enables nothing: pay stays guarded. See
        // checkoutContext.ts, S3.
        try {
          installCheckoutRouteEntry(window, authFetch);
        } catch (err) {
          console.error("[web-install] checkout route entry not installed:", err);
        }

        // Thread DETAIL — msgs, history and carrier scans — loaded when a
        // thread is actually opened rather than for every row at boot.
        //
        // ⚠️ Installed AFTER installDeals, and that ordering is load-bearing:
        // the loader's first pass reads the already-rendered thread list to find
        // the one renderDeals opened by default, and reads the counterpart
        // handle back off THREADS. Before the list installs there is neither.
        //
        // Non-fatal like the two above. The list already shipped a fully-shaped
        // row whose empty msgs/history and null tracking Blake's own code
        // guards, so a failure here leaves the thread readable.
        try {
          installDealThreadLoader(window, authFetch, user.id);
        } catch (err) {
          console.error("[web-install] deal thread detail not installed:", err);
        }

        // ACCEPT / DECLINE / WITHDRAW, doing the real PATCH.
        //
        // ⚠️ IT DEPENDS ON installDealThreadLoader EXISTING, which is why it sits
        // under it: the loader is what stashes __gbzOfferId on the row, and an
        // offer action has nothing to address without it — every click would
        // take the out-loud refusal instead. A dependency in reading order, not
        // a race: both are document-capture listeners and neither observes the
        // other, so the id is stashed when a thread is OPENED regardless of
        // which of the two installed first.
        //
        // It is after installWriteGuard for the reason stated there and at
        // installWizardPublish: the guard kills Blake's fabricator on the way
        // down, this does the real write, and both run because the guard uses
        // stopPropagation and never its Immediate cousin. All three selectors
        // STAY guarded — if this fails to install, the buttons go back to dead
        // rather than to announcing an acceptance the server never recorded.
        try {
          installDealActions(window, authFetch, user.id);
        } catch (err) {
          console.error("[web-install] deal actions not installed:", err);
        }

        // THE $1 IN-PERSON FEE'S PAYMENT ELEMENT (card 1218526698505993), filling
        // the seam installInPerson leaves (window.__gbzInPersonPay). BEFORE it,
        // because a cold #/h/<CODE> link paints its confirm screen during that
        // install and the element mounts on that first paint. Stripe.js loads
        // only when a payer's confirm screen opens, through loadScript. Without
        // a publishable key in this build it installs nothing (and says so in
        // the console); the payer's button then stays disabled and says card
        // payment isn't connected. Its own try/catch: a failure here must not
        // take the in-person screens down with it.
        try {
          installInPersonPay(window, { load: loadScript });
        } catch (err) {
          console.error("[web-install] in-person payment element not installed:", err);
        }

        // IN PERSON (card 1218886635703438): the confirm, code, receipt and
        // error screens' transport, beside the other deal writes and after the
        // thread loader for the same reason as installDealActions (the offer
        // id it needs is stashed by the loader). Its own try/catch: a failure
        // here leaves the "In person" button dead (the module owns the tap and
        // app.js's demo branch forks on the module's presence), never a
        // fabricated confirm. Also the E1 return: after the sign-in reload it
        // reads the scanned link back out of sessionStorage and goes there.
        try {
          installInPerson(window, authFetch, user.id);
        } catch (err) {
          console.error("[web-install] in person not installed:", err);
        }

        // THE COUNTER, in its own try/catch beside the other three. Same
        // dependencies as installDealActions and one more: the counter sheet's
        // own state lives in app.js, so it reads counterFor and composerState
        // through the bridge. Independent of installDealActions — a failure in
        // either must not take the other's controls down with it.
        try {
          installDealCounter(window, authFetch, user.id);
        } catch (err) {
          console.error("[web-install] deal counter not installed:", err);
        }

        // STARTING A NEW OFFER, installed immediately after the counter because
        // the two share one button. `[data-send-offer]` is Send Counter with a
        // counter in progress and Make offer without one; dealCounter answers
        // the first and returns silently for the second, and this answers the
        // second. They are kept apart in the BRIDGE — __gbzComposerCounter and
        // __gbzComposerOffer are mutually exclusive on counterFor — so ordering
        // here is for the reader, not for correctness.
        //
        // 🔑 This is the control card 1218886330579015 was opened for: until it
        // landed, a signed-in visitor could not start an offer on the website at
        // all. Its own try/catch for the usual reason: if it throws, the button
        // falls back to dead rather than to Blake's "escrow reserved" sentence.
        try {
          installMakeOffer(window, authFetch, user.id);
        } catch (err) {
          console.error("[web-install] make offer not installed:", err);
        }

        // FILING A DISPUTE, in its own try/catch like the other deal writes.
        // `#dispute-file` stays guarded: if this fails to install, the button
        // goes back to dead rather than to minting a reference number for a
        // dispute the server never received. See disputeFile.ts.
        try {
          installDisputeFile(window, authFetch, user.id);
        } catch (err) {
          console.error("[web-install] dispute filing not installed:", err);
        }

        // VIEW RECEIPT on a completed deal, read from GET /api/receipts/:offerId
        // into its own store. Never writes a thread's fees, which decide what
        // checkout will open. If this fails to install, View Receipt paints
        // nothing, as before. See receiptView.ts.
        try {
          installReceiptView(window, authFetch);
        } catch (err) {
          console.error("[web-install] view receipt not installed:", err);
        }

        // THE DROP-OFF CODE AND "I'VE DROPPED IT OFF" on the tracking pop-up,
        // for the viewer's OWN leg only. The code is fetched on a tap and never
        // on load, render, poll or signal. If this fails to install, the pop-up
        // paints no code and no button (renderShipPanel needs __gbzShipLeg),
        // as before. See shipDropOff.ts.
        try {
          installShipDropOff(window, authFetch, user.id);
        } catch (err) {
          console.error("[web-install] ship drop-off not installed:", err);
        }

        // BOTH MESSAGE COMPOSERS. Takes userIds because the side rail targets a
        // HANDLE rather than a thread — the deal thread's key carries the
        // counterpart id, the rail's does not, so it falls back to the same
        // handle->id map installFollowUser holds and refuses out loud if that
        // misses too.
        //
        // ⚠️ It registers a KEYDOWN as well as a click, and that is not
        // symmetry for its own sake: #msgrail-in's Enter calls railSend()
        // directly with no click dispatched, so a click listener is blind to
        // the rail's most-used key. #msg-in's Enter synthesises a click and is
        // covered by the click path.
        try {
          installDealMessaging(window, authFetch, user.id, userIds);
        } catch (err) {
          console.error("[web-install] deal messaging not installed:", err);
        }

        // THE OPEN THREAD, RE-READ EVERY 15s so a reply appears without a reload.
        // It shares the send bookkeeping (threadWrites) with the composer, so a
        // poll never lands on top of a message being sent.
        try {
          installDealThreadPoll(window, authFetch, user.id);
        } catch (err) {
          console.error("[web-install] deal thread poll not installed:", err);
        }

        // READ STATE (Blake 2026-10-07, card 1219177682931853): a thread whose
        // messages are on the visitor's screen is read. The dot clears at once on
        // every deal row of that person and the server is told (PATCH
        // /api/conversations/:id/read). Under the loader and the poll because it
        // rides on both: the loader's events answer brings the conversation id,
        // and the poll brings a new line while the thread is open. Like those two,
        // its teardown is not kept: it lives as long as the tab. Not fatal if it
        // fails to install, but not invisible either: the badge still counts by
        // the new rule (that is app.js's), while nothing tells the server and an
        // open clears only the clicked row, in memory, until the next list read.
        try {
          installDealsReadState(window, authFetch);
        } catch (err) {
          console.error("[web-install] deals read state not installed:", err);
        }

        // THE EXCHANGE COUNTER'S PREFILL, corrected from the parent offer.
        //
        // Reads GET /api/offers/:id and fixes what the THREADS row cannot show:
        // the parent's REQUESTED items and its requestedCashAmount, neither of
        // which is on the deal-threads wire (offeredItems.ts filters to
        // side='offered'). Without it the Exchange opens pre-filled with terms
        // that are missing pieces, and looks complete while it does.
        //
        // ⚠️ It SENDS nothing. #ex-send stays guarded and dead; this only makes
        // the screen match the deal, which has to be true before a send is
        // wired at all.
        try {
          installExchangePrefill(window, authFetch);
        } catch (err) {
          console.error("[web-install] exchange prefill not installed:", err);
        }

        // THE EXCHANGE COUNTER'S SEND — the sixth and last of the controls.
        //
        // ⚠️ AFTER installExchangePrefill, and that ordering is a dependency
        // rather than a preference: the send REFUSES when the prefill had to
        // drop one of the viewer's items, and it learns that from the flag the
        // prefill stashes. Installed without it, every counter would look
        // faithful and some would be short.
        try {
          installExchangeSend(window, authFetch, user.id);
        } catch (err) {
          console.error("[web-install] exchange send not installed:", err);
        }

        // LOOKING FOR. Independent of everything above — it feeds the account
        // row's "N active wants" count and the profile/wizard chips, and touches
        // no surface the deals or wallet installs touch, so its ordering here is
        // just "after the money paths" rather than load-bearing.
        //
        // The floor install already CLEARED myLookingFor, so a failure leaves an
        // honest empty list rather than Blake's four fixture wants — which name a
        // specific Omega reference and a specific sampler, and would read as this
        // visitor's own taste.
        try {
          /* Through the wants module's numbered re-read (installed for everyone above), so a boot
             answer landing after a save's re-read cannot overwrite the saved list; the direct
             read is the fallback when the module is not there. */
          const reread = (window as Window & { __gbzWantsReread?: () => Promise<number> }).__gbzWantsReread;
          const wantCount = await (reread ? reread() : installWants(window, authFetch));
          console.info(`[web-install] wants: installed ${wantCount}`);
        } catch (err) {
          console.error("[web-install] wants not installed:", err);
        }

        // THE BELL + ACTIVITY DRAWER. Independent of everything above, same
        // posture: the floor install already cleared NOTIFICATIONS, so a
        // failure leaves an honest empty drawer and no number on the bell rather than
        // Blake's five fabricated rows (one of which names a specific
        // counterpart, @kovafinds, that would read as this visitor's own
        // activity).
        try {
          const notifCount = await installNotifications(window, authFetch, user.id);
          console.info(`[web-install] notifications: installed ${notifCount}`);
        } catch (err) {
          console.error("[web-install] notifications not installed:", err);
        }
        // Mark as read: a tap on a row, and Mark all as read in the drawer head,
        // paint first and then tell the server (card 1219063265423680). Its own
        // try: a list that failed to install still has its drawer wired.
        try {
          installNotificationsRead(window, authFetch, user.id);
        } catch (err) {
          console.error("[web-install] notifications read state not installed:", err);
        }

        // ACCOUNT — identity, shipping address and saved card. v48 (#661)
        // retired ~17 hardcoded PII sites and left these three empty, which
        // renders honest "No shipping address on file" / "No payment method on
        // file" states. This fills them from the real signed-in user.
        //
        // ⚠️ Load-bearing for CHECKOUT, not just #/account: renderCheckout's
        // "Ships To" and "Paying With" blocks read the same three objects, so
        // without this a real visitor reaches a checkout screen that cannot say
        // where the parcel is going.
        //
        // Its own try/catch, same posture as the four above: a profile that
        // fails to load must not take the deals list down with it, and Blake's
        // empty states are already honest.
        try {
          await installAccount(window, authFetch);
        } catch (err) {
          console.error("[web-install] account not installed:", err);
        }
      }
    })().catch((err) => {
      // recon 16, L5: `bootStarted` was never reset, so ANY failure —
      // including a transient network blip — permanently poisoned /web for
      // the tab; only a full reload could retry. Resetting it here means
      // leaving /web and coming back (isActive false then true again) retries
      // the boot instead of staying stuck.
      bootStarted = false;
      setBootError(err instanceof Error ? err.message : String(err));
    });
  }, [isActive, authFetch, loading]);

  // Mirror #app's own data-mode onto the scope root CONTINUOUSLY. v47's
  // render() sets `app.dataset.mode = store.mode` synchronously, ALWAYS
  // before it writes app.innerHTML (app.js:3730 — "mode flag rides THIS
  // app's root, not <html> -- the page is shared now"), on every one of his
  // ~13 view branches, unmodified, unpatched. @scope's
  // `:scope[data-mode="night"]` rules only match the attribute on the scope
  // root itself (this component's own wrapper div, below), never a
  // descendant, so #app's attribute does nothing for them on its own — this
  // observer is what actually carries Blake's clock-driven value up to the
  // element those rules key off.
  //
  // DELIBERATELY follows #app, not document.documentElement (<html> is read
  // once, as the starting mode, by the effect after this one) — see the
  // HISTORICAL note below for the v44 mechanism this replaces, and App.tsx's
  // own comment on the isWebInstall branch for why mounting a
  // <ThemeProvider> here (the more obvious-looking fix) was rejected: it
  // would add a second theme owner on the site's highest-traffic route (and,
  // until 2026-09-14, a geolocation prompt and an ip-api.com fetch). This
  // effect adds none of that — it is pure DOM mirroring of a value Blake's
  // own file already computes.
  //
  // BEFORE Blake's first render() call (the whole async boot sequence:
  // stylesheet loads, data.js, the floor fetch, app.js), #app carries no
  // data-mode attribute at all, so the initial sync() call is a no-op and
  // the wrapper renders under :scope's unconditional base rules — which
  // styles.css's own structure (`:root { day colors }` / `[data-mode="night"]
  // { overrides }`) means DAY colors, not a broken/unstyled state. That
  // window WAS visible: #app is empty until render(), but the wrapper's own
  // `background: var(--canvas)` painted the day cream once styles.scoped.css
  // loaded, for night visitors too (card 1219711747205363). The effect after
  // this one starts the wrapper in the page's mode instead.
  /* 🔴 HIS BODY-APPENDED OVERLAYS FALL OUTSIDE THE CSS SCOPE, so they render
     unstyled at the bottom of the document and read as a dead button. Three of
     them: the Exchange REVIEW OFFER modal, its success screen, and the gachapon.

     Its OWN effect, not folded into the data-mode sync below, because the two
     answer different questions and must not share a bail: that one returns early
     when #app is missing, and an overlay can be appended by a handler at a moment
     when this component has re-rendered but appRef has not been reattached.
     Depends on nothing, so it installs once and lives as long as the host.

     See scopeRescue.ts. Reported as "REVIEW OFFER does nothing" — the listener
     fires and the markup inserts; it is invisible, not dead. */
  useEffect(() => {
    return installScopeRescue(window, containerRef.current);
  }, []);

  /* "Skip for now" on the post-register Stripe card actually leaves the flow.

     app.js:6709 is `location.assign("/#/")`, which on a page already sitting on
     a hash route changes nothing but the hash and does not reload, so the newly
     created account is never picked up and the button reads as dead. Same defect
     as the sign-in hang at app.js:6688, which is fixed in authSeam.tsx because we
     own that promise; this one has no seam call, so it needs a listener.

     Its own effect, and depends on nothing, for the same reason as scopeRescue
     above: it must be alive whenever his handler can fire, independent of any
     ref being attached. See authSkipReload.ts. */
  useEffect(() => {
    return installAuthSkipReload(window);
  }, []);

  useEffect(() => {
    const root = containerRef.current;
    const app = appRef.current;
    if (!root || !app) return;
    const sync = () => {
      const mode = app.getAttribute("data-mode");
      if (mode) root.setAttribute("data-mode", mode);
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(app, { attributes: true, attributeFilter: ["data-mode"] });
    return () => observer.disconnect();
  }, []);

  /* Card 1219711747205363: until his first render() the root drew the scoped
     sheet's DEFAULT canvas, the day cream, the moment that sheet loaded, so a
     night visitor saw cream between the dark first paint and the app. Each
     time the install becomes the page, and his render() has not set a mode
     yet, start the root in <html>'s mode: public/first-paint-mode.js set it
     before first paint by store.mode's rule, and ThemeProvider keeps it on the
     other pages. Read here only, never observed: #app owns the root from his
     first render on. */
  useEffect(() => {
    if (!isActive) return;
    const root = containerRef.current;
    const app = appRef.current;
    if (!root || !app || app.hasAttribute("data-mode")) return;
    const first = document.documentElement.getAttribute("data-mode");
    if (first === "day" || first === "night") root.setAttribute("data-mode", first);
  }, [isActive]);

  // HISTORICAL — this mirror used to read document.documentElement, and a
  // SECOND contamination path (distinct from the CSS leak the Phase 0 spike
  // measured) used to exist alongside it: v44's public/web/app.js:3704 ran
  // `document.documentElement.dataset.mode = store.mode` on every one of
  // Blake's own render() calls, writing directly to the SAME <html> element
  // ThemeContext.tsx (src/context/ThemeContext.tsx) uses to theme every
  // OTHER route — and /web/"/" had no ThemeProvider of its own, so nothing
  // fought that write while you were actually on this page, but app.js's two
  // global handlers (the cross-route guard above) outlived the route and
  // kept re-firing render() on other routes too, repainting <html> out from
  // under whichever ThemeProvider WAS active there.
  //
  // v47 deleted that line outright — his new render() writes
  // `app.dataset.mode = store.mode` onto `#app` instead, a child of THIS
  // component's own scope wrapper, invisible to <html> and to every other
  // route regardless of the cross-route guard. Nothing on "/" drove <html
  // data-mode> any more once that write moved, which is what made the
  // route stick at index.html's hardcoded "night" 24/7 (see App.tsx's own
  // comment and src/__tests__/webInstallThemeMode.test.tsx for the
  // reproduction). The fix above stays entirely inside this component's own
  // scope instead of touching <html> at all — there is no longer a second
  // writer to contend with, because there is no longer a <html> writer on
  // this route in the first place.
  return (
    <>
      <div
        ref={containerRef}
        className={SCOPE_CLASS}
        data-web-install-root="1"
        style={{ display: isActive ? "block" : "none" }}
      >
        <div id="app" ref={appRef} />
        {bootError && (
          <div role="alert" style={{ padding: 16, fontFamily: "monospace" }}>
            <p>Could not load the floor.</p>
            <p>{bootError}</p>
          </div>
        )}
      </div>
    </>
  );
}
